import {notifications} from '@mantine/notifications'
import {useEffect, useMemo, useState} from 'react'

import {useRentalAssetTypes} from 'src/assets/rental-assets/use-rental-asset-types'
import {useWorkerTypes} from 'src/assets/worker-types/use-worker-types'
import {parseApiDate, toApiDateString} from 'src/jobs/job-schedule-format'
import {useProjectBillingTerms} from 'src/projects/use-projects'

import {
    buildDraftJobs as generateJobs,
    buildSummaryCounts,
    schedulingCadenceForApi,
    templateRequirementsToApi,
    toggleSkippedServiceDate,
} from './build-out-logic'
import type {BuildOutRequirement, BuildOutScheduling, GeneratedJob} from './build-out-types'
import {describeScheduleChanges} from './change-preview-logic'
import {lineRequirementsFromSchedule, schedulingFromServiceSchedule} from './schedule-draft-logic'
import type {ChangePreview, ServiceSchedule, ServiceScheduleChangeInput} from './schemas'
import {
    useApplyServiceScheduleChange,
    usePreviewServiceScheduleChange,
    useServiceSchedule,
} from './use-service-schedules'

function buildChangeInput(
    scheduling: BuildOutScheduling,
    schedule: ServiceSchedule,
    poNumber: string,
): ServiceScheduleChangeInput {
    return {
        endDate: scheduling.endDate,
        cadenceWeekdays: schedulingCadenceForApi(scheduling),
        serviceNotes: scheduling.serviceNotes,
        skippedServiceDates: scheduling.skippedServiceDates,
        ...templateRequirementsToApi(scheduling.serviceTemplate),
        ...(poNumber.trim() !== (schedule.poNumber ?? '')
            ? {poNumber: poNumber.trim() || null}
            : {}),
    }
}

function schedulingAfterApply(
    schedule: ServiceSchedule,
    current: BuildOutScheduling,
): BuildOutScheduling {
    return {
        ...current,
        endDate: parseApiDate(schedule.endDate),
        cadence: {
            ...current.cadence,
            weekdays: schedulingFromServiceSchedule(schedule).cadence.weekdays,
        },
        serviceTypeId: schedule.serviceTypeId,
        serviceNotes: schedule.notes ?? '',
        deployNotes: schedule.jobs.find((job) => job.scheduleRole === 'deploy')?.notes ?? '',
        pickupNotes: schedule.jobs.find((job) => job.scheduleRole === 'pickup')?.notes ?? '',
        skippedServiceDates: schedule.jobs
            .filter((job) => job.scheduleRole === 'service' && job.status === 'cancelled')
            .map((job) => job.startDate),
    }
}

type UseServiceScheduleEditorOptions = {
    scheduleId: string
    lineRequirements?: BuildOutRequirement[]
    enabled?: boolean
}

export function useServiceScheduleEditor({
    scheduleId,
    lineRequirements,
    enabled = true,
}: UseServiceScheduleEditorOptions) {
    const {
        data: schedule,
        isLoading: isLoadingSchedule,
        isError: isScheduleError,
    } = useServiceSchedule(scheduleId, {enabled})
    const {data: billingTerms = []} = useProjectBillingTerms(schedule?.projectId ?? '', {
        enabled: enabled && schedule != null,
    })
    const {data: workerTypes = [], isLoading: isLoadingWorkerTypes} = useWorkerTypes()
    const {data: rentalAssetTypes = [], isLoading: isLoadingRentalAssetTypes} =
        useRentalAssetTypes()

    const [scheduling, setScheduling] = useState<BuildOutScheduling | null>(null)
    const [initialized, setInitialized] = useState(false)
    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewData, setPreviewData] = useState<ChangePreview | null>(null)
    const [poNumber, setPoNumber] = useState('')

    const previewChange = usePreviewServiceScheduleChange(scheduleId)
    const applyChange = useApplyServiceScheduleChange(scheduleId)

    useEffect(() => {
        if (!enabled || initialized || isLoadingSchedule || schedule == null) {
            return
        }

        setScheduling(schedulingFromServiceSchedule(schedule))
        setPoNumber(schedule.poNumber ?? '')
        setInitialized(true)
    }, [enabled, initialized, isLoadingSchedule, schedule])

    /**
     * The terms this schedule was stamped with. Versioning is an implementation detail: a revision
     * on the project creates a new immutable row, and existing schedules keep the row they carry.
     */
    const scheduleBillingTerms = useMemo(
        () => billingTerms.find((terms) => terms.id === schedule?.projectBillingTermsId) ?? null,
        [billingTerms, schedule],
    )

    const workerTypeNames = useMemo(
        () => new Map(workerTypes.map((type) => [type.id, type.name])),
        [workerTypes],
    )
    const equipmentTypeNames = useMemo(
        () => new Map(rentalAssetTypes.map((type) => [type.id, type.name])),
        [rentalAssetTypes],
    )
    const workerTypeOptions = useMemo(
        () => workerTypes.map((type) => ({value: type.id, label: type.name})),
        [workerTypes],
    )
    const equipmentTypeOptions = useMemo(
        () => rentalAssetTypes.map((type) => ({value: type.id, label: type.name})),
        [rentalAssetTypes],
    )

    const generatedJobs = useMemo(() => {
        if (scheduling == null || schedule == null) {
            return []
        }

        const requirements =
            lineRequirements ?? (schedule == null ? [] : lineRequirementsFromSchedule(schedule))

        return generateJobs(scheduling, requirements)
    }, [lineRequirements, schedule, scheduling])

    const summaryCounts = useMemo(() => buildSummaryCounts(generatedJobs), [generatedJobs])

    const changeSummary = useMemo(() => {
        if (schedule == null || scheduling == null) {
            return []
        }

        return describeScheduleChanges({
            schedule,
            scheduling,
            workerTypeNames,
            equipmentTypeNames,
        })
    }, [equipmentTypeNames, schedule, scheduling, workerTypeNames])

    const isLoading =
        !enabled ||
        isLoadingSchedule ||
        isLoadingWorkerTypes ||
        isLoadingRentalAssetTypes ||
        !initialized ||
        scheduling == null

    function closePreview() {
        setPreviewOpen(false)
        setPreviewData(null)
    }

    async function handleReviewChanges() {
        if (scheduling == null || schedule == null) {
            return
        }

        setPreviewOpen(true)
        setPreviewData(null)
        try {
            const preview = await previewChange.mutateAsync(
                buildChangeInput(scheduling, schedule, poNumber),
            )
            setPreviewData(preview)
        } catch (error) {
            setPreviewOpen(false)
            notifications.show({
                title: 'Could not preview changes',
                message: error instanceof Error ? error.message : 'Something went wrong.',
                color: 'red',
            })
        }
    }

    async function handleApplyChanges() {
        if (scheduling == null || schedule == null) {
            return
        }

        try {
            const updatedSchedule = await applyChange.mutateAsync(
                buildChangeInput(scheduling, schedule, poNumber),
            )
            setPreviewOpen(false)
            setPreviewData(null)
            notifications.show({
                title: 'Changes applied',
                message: 'The service schedule was updated.',
                color: 'green',
            })
            setScheduling((current) =>
                current == null ? current : schedulingAfterApply(updatedSchedule, current),
            )
            setPoNumber(updatedSchedule.poNumber ?? '')
        } catch (error) {
            notifications.show({
                title: 'Could not apply changes',
                message: error instanceof Error ? error.message : 'Something went wrong.',
                color: 'red',
            })
        }
    }

    function handleToggleSkip(job: GeneratedJob, skipped: boolean) {
        if (scheduling == null || job.stage !== 'service') {
            return
        }

        const dateKey = toApiDateString(job.date)
        const currentlySkipped = scheduling.skippedServiceDates.includes(dateKey)
        if (currentlySkipped === skipped) {
            return
        }

        setScheduling(toggleSkippedServiceDate(scheduling, job.date))
    }

    return {
        schedule,
        scheduleBillingTerms,
        poNumber,
        setPoNumber,
        scheduling,
        setScheduling,
        generatedJobs,
        summaryCounts,
        changeSummary,
        isLoading,
        isScheduleError,
        workerTypeNames,
        equipmentTypeNames,
        workerTypeOptions,
        equipmentTypeOptions,
        previewOpen,
        setPreviewOpen,
        closePreview,
        previewData,
        previewChange,
        applyChange,
        handleReviewChanges,
        handleApplyChanges,
        handleToggleSkip,
    }
}

export type ServiceScheduleEditorState = ReturnType<typeof useServiceScheduleEditor>
